import {
  useFormItemAccessibility,
  useFormItemEnabled,
  useInputController,
} from '@framework/form';
import { Input, InputProps } from 'antd';
import { clamp } from 'lodash';
import { useCallback } from 'react';

export interface IRepeatControllerInputProps
  extends Omit<InputProps, 'name' | 'type'> {
  name: string;
  /**
   * Funzione che applica le modifiche del campo di input
   * alla lista target.
   */
  applyChangeToList: (
    value: number | null,
    /** Permette di modificare il valore del RepeatControllerInput */
    onChange: (value: number) => void,
  ) => Promise<void>;
}

/**
 * Number input specifico per la gestione del RepeatController.
 */
export function RepeatControllerInput(props: IRepeatControllerInputProps) {
  const { name, applyChangeToList, ...inputProps } = props;

  const { enabled, layoutEnabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const accessibility = useFormItemAccessibility();

  // Gestisce le modifiche del campo di input.
  const handleChange = useCallback(
    (event: any) => {
      if (event.target.value === '') {
        onChange(null);
        return;
      }
      const parsedValue = Number(event.target.value);

      // Il minimo e il massimo creano dei bug in fase di scrittura. Da rivedere.
      if (inputProps.max != null && parsedValue > Number(inputProps.max)) {
        event.target.value = inputProps.max;
        onChange(event);
        return;
      }

      if (inputProps.min != null && parsedValue < Number(inputProps.min)) {
        event.target.value = inputProps.min;
        onChange(event);
        return;
      }

      onChange(event);
    },
    [inputProps, onChange],
  );

  // Le modifiche alla lista vengon applicate solo al blur. In questo modo
  // è più facile per l'utente modificare il campo senza renderizzare tutta
  // la lista ad ogni keypress ed evitando il mostrarsi di errori continui.
  const handleBlur = useCallback(
    (event: any) => {
      const eventValue = event.target.value;

      const parsedValue = Number(eventValue);

      handleChange(event);

      // Utilizzo una promise per evitare problemi di tipo nel onBlur dell'Input.
      new Promise(() => {
        applyChangeToList(
          clamp(
            parsedValue,
            Number(inputProps.min ?? 0),
            Number(inputProps.max ?? Infinity),
          ),
          onChange,
        );
      }).then(() => {
        onBlur();
      });
    },
    [
      applyChangeToList,
      handleChange,
      inputProps.max,
      inputProps.min,
      onBlur,
      onChange,
    ],
  );

  return (
    <Input
      size="large"
      {...accessibility.attributes}
      ref={ref}
      disabled={enabled === false || layoutEnabled === false}
      {...inputProps}
      value={inputProps.value ?? value}
      onChange={handleChange}
      onBlur={handleBlur}
      name={name}
      suffix={<span aria-hidden="true" />} // See https://ant.design/components/input#why-input-lose-focus-when-change-prefixsuffixshowcount
      type="number"
    />
  );
}
